import { formatPhoneForStorage } from './phoneUtils';

/**
 * Parse un nom complet en firstName et lastName
 * Gère les cas : "Jean Dupont", "Jean-Pierre Martin", "M. Jean Dupont", etc.
 */
export function parseFullName(name: string): { firstName: string; lastName: string } {
  if (!name || name.trim() === '') {
    return { firstName: '', lastName: '' };
  }

  // Nettoyer le nom (enlever préfixes M./Mme, espaces multiples)
  let cleanName = name.trim()
    .replace(/^(M\.|Mme|Mr|Monsieur|Madame)\s+/i, '')
    .replace(/\s+/g, ' ');

  const parts = cleanName.split(' ').filter(p => p.length > 0);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    // Un seul mot = tout dans lastName
    return { firstName: '', lastName: parts[0] };
  }

  // Premier mot = firstName, reste = lastName
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');

  return { firstName, lastName };
}

/**
 * Parse une adresse depuis le champ "Ville" du CSV
 * Peut contenir : "27 Rue Eugène Fournière 69100 Villeurbanne" ou juste "Villeurbanne"
 */
export function parseAddress(cityField: string): { address: string; city: string; zipCode?: string } {
  if (!cityField || cityField.trim() === '') {
    return { address: '', city: '' };
  }

  const cleanField = cityField.trim();

  // Pattern pour détecter un code postal (5 chiffres)
  const zipCodePattern = /\b(\d{5})\b/;
  const zipMatch = cleanField.match(zipCodePattern);

  let zipCode: string | undefined;
  let address = cleanField;
  let city = cleanField;

  if (zipMatch) {
    zipCode = zipMatch[1];
    const zipIndex = cleanField.indexOf(zipCode);

    // Extraire la ville (après le code postal)
    const afterZip = cleanField.substring(zipIndex + 5).trim();
    if (afterZip) {
      city = afterZip;
      // L'adresse est tout ce qui précède le code postal
      address = cleanField.substring(0, zipIndex).trim();
    } else {
      // Pas de ville après le code postal, chercher avant
      const beforeZip = cleanField.substring(0, zipIndex).trim();
      // Si ça ressemble à une adresse (contient "rue", "avenue", etc.)
      if (beforeZip.match(/\b(rue|avenue|boulevard|place|chemin|route|impasse|allée)\b/i)) {
        address = beforeZip;
        // La ville pourrait être le dernier mot avant le code postal
        const words = beforeZip.split(/\s+/);
        city = words[words.length - 1] || cleanField;
      } else {
        address = beforeZip;
        city = beforeZip.split(/\s+/).pop() || cleanField;
      }
    }
  } else {
    // Pas de code postal, essayer de détecter si c'est une adresse ou juste une ville
    // Si ça contient des mots-clés d'adresse, c'est probablement une adresse complète
    if (cleanField.match(/\b(rue|avenue|boulevard|place|chemin|route|impasse|allée|quartier)\b/i)) {
      address = cleanField;
      // Extraire la ville (dernier mot ou partie après une virgule)
      const parts = cleanField.split(/[,\s]+/);
      city = parts[parts.length - 1] || cleanField;
    } else {
      // Probablement juste une ville
      city = cleanField;
      address = cleanField;
    }
  }

  return {
    address: address || city,
    city: city || '',
    zipCode
  };
}

/**
 * Normalise un numéro de téléphone depuis le CSV
 * Gère "NA", formats variés, etc.
 */
export function normalizePhone(phone: string): string {
  if (!phone || phone.trim() === '' || phone.toUpperCase() === 'NA') {
    return '';
  }

  return formatPhoneForStorage(phone);
}

/**
 * Normalise un email depuis le CSV
 * Gère "NA", espaces, etc.
 */
export function normalizeEmail(email: string): string {
  if (!email || email.trim() === '' || email.toUpperCase() === 'NA') {
    return '';
  }

  return email.trim().toLowerCase();
}

/**
 * Parse une date au format français (DD/MM/YYYY) en ISO string
 */
export function parseFrenchDate(dateStr: string): string | null {
  if (!dateStr || dateStr.trim() === '') {
    return null;
  }

  // Format attendu : DD/MM/YYYY ou DD/MM/YYYY HH:MM
  const match = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(am|pm))?)?/i);
  
  if (!match) {
    return null;
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const year = parseInt(match[3], 10);
  
  let hour = match[4] ? parseInt(match[4], 10) : 0;
  const minute = match[5] ? parseInt(match[5], 10) : 0;
  const second = match[6] ? parseInt(match[6], 10) : 0;
  const ampm = match[7]?.toLowerCase();

  // Gérer AM/PM
  if (ampm === 'pm' && hour < 12) {
    hour += 12;
  } else if (ampm === 'am' && hour === 12) {
    hour = 0;
  }

  const date = new Date(year, month, day, hour, minute, second);
  
  // Vérifier que la date est valide
  if (isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

/**
 * Extrait une URL Leboncoin depuis le champ "Pièces jointes"
 */
export function extractLeboncoinUrl(attachments: string): string | null {
  if (!attachments || attachments.trim() === '') {
    return null;
  }

  // Chercher une URL leboncoin
  const urlPattern = /https?:\/\/(www\.)?leboncoin\.fr\/[^\s]+/i;
  const match = attachments.match(urlPattern);
  
  return match ? match[0] : null;
}
